import { type AxiosResponse } from 'axios';
import { BASE_URL } from '@avanta/shared/constants/common';
import { service } from '@avanta/shared/services/api';
import {
  type Speciality,
  type Appointment,
  type AppointmentPayload,
  type CancelAppointmentPayload,
  type EditAppointmentPayload,
  type Center,
  type SessionDetails,
  type DailySlot,
  type TopPsychologist,
  type BookingOptions,
} from '@avanta/shared/types/appointments';
import { type Psychologist, type ContractType } from '@avanta/shared/types/psychologists';

const API_URL = `${BASE_URL}appointments`;

export type AppointmentServiceTypes = {
  getAppointments: (
    patientId: string,
    psychologistId?: string
  ) => Promise<AxiosResponse<Appointment[]>>;
  cancel: ({
    psychologistId,
    appointmentId,
    patientId,
  }: CancelAppointmentPayload) => Promise<AxiosResponse<void>>;
  getSessionDetails: (sessionId: string) => Promise<AxiosResponse<SessionDetails>>;
  getCenterDetails: (centerId: number) => Promise<AxiosResponse<Center>>;
  book: (appointment: AppointmentPayload) => Promise<AxiosResponse<Appointment>>;
  edit: (appointment: EditAppointmentPayload) => Promise<AxiosResponse<void>>;
  getSpecialities: (patientId: string) => Promise<AxiosResponse<Speciality[]>>;
  getCenters: (specialityId: string | number) => Promise<AxiosResponse<Center[]>>;
  getFreeHoursAvailability: (params: {
    patientId: string;
    specialityId: string | number;
    centerId: number | string;
    spokenLanguage: string;
    foreignFreelance?: boolean;
  }) => Promise<AxiosResponse<{ dailySlots: DailySlot[] }>>;
  getTopPsychologistsAvailability: (params: {
    startDate: string;
    days: number;
    specialityId: string | number;
    centerId: number | string;
    patientId: string;
    psychoFullName?: string;
    contractType?: ContractType;
    spokenLanguage?: string;
    slotHour?: string;
  }) => Promise<AxiosResponse<TopPsychologist[]>>;
  getPsychologistsByName: ({
    name,
    insurance,
  }: {
    name: string;
    insurance?: boolean;
  }) => Promise<AxiosResponse<Psychologist[]>>;
  getBookingOptionsByPsycho: ({
    psychologistId,
    insurance,
  }: {
    psychologistId: string;
    insurance?: boolean;
  }) => Promise<AxiosResponse<BookingOptions>>;
};

export const AppointmentService: AppointmentServiceTypes = {
  getAppointments: async (patientId, psychologistId) =>
    service.get(`${API_URL}/patients/${patientId}`, { params: { psychologistId } }),
  cancel: async body => service.post(`${API_URL}/cancel-from-patient`, body),
  getSessionDetails: async sessionId =>
    service.get(`${BASE_URL}appointmentSession/any/sessions/${sessionId}/patient`),
  getCenterDetails: async centerId => service.get(`${API_URL}/work-centers/${centerId}`),
  book: async appointment => await service.post(`${API_URL}/book`, appointment),
  edit: async appointment => service.post(`${API_URL}/change-from-patient`, appointment),
  getSpecialities: async patientId => service.get(`${API_URL}/specialities/patients/${patientId}`),
  getCenters: async specialityId =>
    service.get(`${API_URL}/work-centers/speciality/${specialityId}`),
  getFreeHoursAvailability: async ({
    patientId,
    specialityId,
    centerId,
    spokenLanguage,
    foreignFreelance,
  }) =>
    service.get(`${API_URL}/availability/free-hours`, {
      params: {
        patientId,
        specialityId,
        centerId,
        spokenLanguage,
        foreignFreelance,
      },
    }),
  getTopPsychologistsAvailability: async ({
    startDate,
    days,
    specialityId,
    centerId,
    psychoFullName,
    spokenLanguage,
    contractType,
    patientId,
    slotHour,
  }) =>
    service.get(`${API_URL}/availability/top`, {
      params: {
        start: startDate,
        days,
        patientId,
        psychoFullName,
        specialityId,
        centerId,
        spokenLanguage,
        contractType,
        slotHour,
      },
    }),
  getPsychologistsByName: async ({ name, insurance }) =>
    service.get(`${API_URL}/psychologists/search`, { params: { name, insurance } }),
  getBookingOptionsByPsycho: async ({ psychologistId, insurance }) =>
    service.get(`${API_URL}/psychologists/${psychologistId}/booking-options`, {
      params: { insurance },
    }),
};
